import {
  ChevronLast,
  ChevronFirst,
  Menu,
  ChevronRight,
  ChevronDown,
  User,
  LogOut,
} from "lucide-react";
import { useContext, createContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import { useUser } from "../UserContext";

const SidebarContext = createContext();

export default function Sidebar({ children, expanded, setExpanded }) {
  const { user, logout } = useUser();
  const navigate = useNavigate();

  const [dropdownVisible, setDropdownVisible] = useState(false);

  const toggleDropdown = () => {
    setDropdownVisible((prev) => !prev);
  };

  return (
    <aside className="h-screen fixed top-0 left-0 z-50">
      <div
        className={`bg-[#198754] fixed w-full p-4 hidden md:block ${
          expanded ? "pl-64" : "pl-20"
        }`}
      >
        <div className="flex justify-between">
          <span className="flex gap-2 items-center">
            <p className="text-white font-semibold">{user && user.name}</p>
            <p className="bg-white px-2 rounded-md text-sm">
              {user && user.studentNumber}
            </p>
          </span>
          <div className="mr-6 cursor-pointer" onClick={toggleDropdown}>
            <User color="white" />
          </div>
          {dropdownVisible && (
            <div className="absolute top-full right-2 bg-white border rounded shadow-md">
              <ul className="py-1">
                <li
                  onClick={() => {
                    logout();
                    sessionStorage.removeItem("user");
                    navigate("/login");
                  }}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center flex-row"
                >
                  <LogOut size={16} className="mr-2.5" /> Logout
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className={`bg-[#198754] fixed w-full p-4 md:hidden`}>
        <div className="flex justify-between items-center">
          <div>
            <button
              onClick={() => setExpanded((curr) => !curr)}
              className="md:hidden" // Only visible on mobile (md:hidden)
            >
              <Menu color="white" />
            </button>
          </div>
          <div className="mr-2 cursor-pointer" onClick={toggleDropdown}>
            <User color="white" />
          </div>
          {dropdownVisible && (
            <div className="absolute top-full right-0 bg-white border rounded shadow-md">
              <ul className="py-1">
                <li
                  onClick={() => {
                    logout();
                    sessionStorage.removeItem("user");
                    navigate("/login");
                  }}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center flex-row"
                >
                  <LogOut size={16} className="mr-2.5" /> Logout
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar navigation */}
      <nav
        className={`h-full flex flex-col bg-white border-r shadow-sm fixed ${
          expanded ? "block" : "hidden md:block"
        }`}
      >
        {" "}
        {/* Show sidebar on mobile when expanded is true */}
        <div className="px-4 py-3 pb-2 flex justify-between items-center">
          {" "}
          {/* Hide logo on mobile */}
          <img
            src={logo}
            className={`overflow-hidden transition-all ${
              expanded ? "w-40" : "w-0"
            }`}
            alt=""
          />
          <button
            onClick={() => setExpanded((curr) => !curr)}
            className="p-1.5 rounded-lg bg-gray-50 hover:bg-gray-100" // Hide menu button on mobile
          >
            {expanded ? (
              <ChevronFirst color="#198754" />
            ) : (
              <ChevronLast color="#198754" />
            )}
          </button>
        </div>
        <div
          className={`pt-1 px-4 pb-2 flex items-center ${
            !expanded && "w-0 hidden"
          }`}
        >
          <h2 className="text-m font-semibold text-gray-500">
            Attendance Tracking System
          </h2>
        </div>
        <SidebarContext.Provider value={{ expanded, setExpanded }}>
          <ul className="flex-1 px-3">{children}</ul>
        </SidebarContext.Provider>
      </nav>
    </aside>
  );
}

export function SidebarItem({ icon, text, active, dropdownItems, path = "/" }) {
  const { expanded } = useContext(SidebarContext);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const navigate = useNavigate(); // Initialize navigate function

  const toggleDropdown = () => {
    setDropdownVisible((prev) => !prev);
  };

  const handleDropdownItemClick = (index) => {
    navigate(`/class/${index}`);
  };

  const navigateHandler = (path) => {
    navigate(path);
  };

  return (
    <div>
      <li
        className={`
              relative flex items-center py-2 px-3 my-1
              font-regular rounded-md cursor-pointer
              transition-colors group
              ${
                active
                  ? "bg-gradient-to-tr from-indigo-200 to-indigo-100 text-indigo-800"
                  : "hover:bg-indigo-50 text-gray-600"
              }
          `}
        onClick={dropdownItems ? toggleDropdown : () => navigateHandler(path)} // Toggle dropdown visibility on click
      >
        {icon}
        <span
          className={`overflow-hidden transition-all ${
            expanded ? "w-40 ml-3" : "w-0"
          }`}
        >
          {text}
        </span>

        {dropdownItems && ( // Render ChevronDown or ChevronRight based on dropdown visibility
          <div className={`absolute right-2 ${expanded ? "" : "hidden"}`}>
            {dropdownVisible ? (
              <ChevronDown size={18} />
            ) : (
              <ChevronRight size={18} />
            )}
          </div>
        )}

        {!expanded && (
          <div
            className={`
                absolute left-full rounded-md px-2 py-1 ml-6
                bg-indigo-100 text-indigo-800 text-sm
                invisible opacity-20 -translate-x-3 transition-all
                group-hover:visible group-hover:opacity-100 group-hover:translate-x-0
            `}
          >
            {text}
          </div>
        )}
      </li>

      {dropdownItems &&
        dropdownVisible && ( // Render dropdownItems only if dropdownVisible is true
          <ul className={`bg-gray-100 ${!expanded && "hidden"}`}>
            {dropdownItems.map((item, index) => (
              <div key={index}>
                <li
                  className="px-10 py-3 text-sm hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleDropdownItemClick(index)} // Handle dropdown item click
                >
                  {`> `}
                  {item}
                </li>
                {index !== dropdownItems.length - 1 && <hr />}
              </div>
            ))}
          </ul>
        )}
    </div>
  );
}
